import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactElement, ReactNode } from 'react';
import { apiGet, apiPost } from '../api/client.js';
import { AUTH_LOADING } from './types.js';
import type { AuthStatus } from './types.js';

export interface AuthContextValue {
  status: AuthStatus | typeof AUTH_LOADING;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [status, setStatus] = useState<AuthStatus | typeof AUTH_LOADING>(
    AUTH_LOADING,
  );

  const refresh = useCallback(async () => {
    try {
      const data = await apiGet<AuthStatus>('/api/auth/status');
      setStatus(data);
    } catch {
      setStatus({ setupRequired: true, authenticated: false, mustResetPassword: false });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost<{ ok: boolean }>('/api/auth/logout', {});
    } finally {
      await refresh();
    }
  }, [refresh]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, refresh, logout }),
    [status, refresh, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
